import Cookies from 'js-cookie'
import gql from 'graphql-tag'
import { ApolloClient } from 'apollo-client'

export const AUTH_TOKEN_KEY = 'login-token'
export const REFRESH_TOKEN_KEY = 'refresh-token'

const {
  VUE_APP_IDP_URL: idpUrl,
  VUE_APP_IDP_TENANT_ID: tenantId,
  VUE_APP_IDP_APPLICATION_ID: clientId,
  VUE_APP_IDP_REDIRECT_URI: redirectURI,
} = process.env

export const LOGIN_URL = `${idpUrl}/oauth2/authorize?tenantId=${tenantId}&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectURI as string)}&response_type=code&scope=offline_access`
export const FORGOT_PASSWORD_URL = `${idpUrl}/password/forgot?tenantId=${tenantId}&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectURI as string)}&response_type=code&scope=offline_access`
export const LOGOUT_URL = `${idpUrl}/oauth2/logout?tenantId=${tenantId}&client_id=${clientId}&post_logout_redirect_uri=${encodeURIComponent(redirectURI as string)}`

let authToken: string
let refreshToken: string

const executeLoginMutation = (client: ApolloClient<unknown>, id: string, secret: string) => {
  return client.mutate({
    // Query
    mutation: gql`mutation login{
      login(
        loginId: "${id}"
        password: "${secret}"
      ) {
        user {
          id
          email
          registrations {
            id
          }
        },
        token
        refreshToken
      }
    }`
  })
}

export const setTokens = (nextToken: string, nextRefreshToken: string): void => {
  authToken = nextToken
  refreshToken = nextRefreshToken
}

export const getToken = (): string => authToken
export const getRefreshToken = (): string => refreshToken
export const clearTokens = (): void => {
  authToken = ''
  refreshToken = ''
}

const POST_LOGIN_REDIRECT_KEY = 'supernova_post_login_redirect'

export const setPostLoginRedirect = (path: string): void => {
  localStorage.setItem(POST_LOGIN_REDIRECT_KEY, path)
}

export const popPostLoginRedirect = (): string | null => {
  const ret = localStorage.getItem(POST_LOGIN_REDIRECT_KEY)
  localStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  return ret
}

export async function login (client: ApolloClient<unknown>, id: string, secret: string): Promise<string> {
  const request = await executeLoginMutation(client, id, secret)
  const token = request.data?.login?.token
  authToken = token
  return token
}

export function hasValidLogin (): boolean {
  return !!authToken && (parseJwt(authToken) || {}).exp * 1000 > Date.now()
}

export function logout (): void {
  const cookieKeys = [
    'access-token',
    AUTH_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    'login-id',
    'user-id',
  ]
  cookieKeys.forEach(cookie => {
    Cookies.remove(cookie)
  })

  const storageKeys = [
    AUTH_TOKEN_KEY
  ]
  storageKeys.forEach(key => {
    localStorage.removeItem(key)
  })

  location.replace(LOGOUT_URL)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseJwt (token: string):Record<string, any>|false {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))
    return JSON.parse(jsonPayload)
  } catch {
    return false
  }
}

export function pendoInitialize (): void {
  if (!authToken) {
    console.error('Token not found in memory!')
  } else {
    const parsed = parseJwt(authToken)
    if (!parsed) {
      console.error('Failed to load JWT')
    } else {
      const userEmail = parsed.email
      if (!userEmail) {
        console.error('Failed to initialize pendo, access token not in cookies')
      } else {
        console.log(`initializing pendo - current user: ${userEmail}`)
        pendo.initialize({
          visitor: {
            id: userEmail
          },
          account: {
            id: userEmail
          }
        })
      }
    }
  }
}
